import React, { useState } from "react";
import api, { formatApiErrorDetail as fmtErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Paperclip, X } from "lucide-react";
import { toast } from "sonner";
import { REIMB_CATEGORIES, useCurrency } from "@/lib/requests";

const MAX = 10 * 1024 * 1024;

export default function ReimbursementDialog({ onClose, onCreated }) {
  const cur = useCurrency();
  const [amount, setAmount] = useState("");
  const [category, setCategory] = useState("Travel");
  const [date, setDate] = useState(new Date().toLocaleDateString("en-CA"));
  const [desc, setDesc] = useState("");
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(null);

  const pick = (f) => {
    if (!f) return;
    if (f.size > MAX) { toast.error("Receipt must be 10 MB or smaller"); return; }
    setFile(f);
  };

  const submit = async () => {
    if (!(Number(amount) > 0)) { toast.error("Enter a valid amount"); return; }
    if (!desc.trim()) { toast.error("Please add a description"); return; }
    const fd = new FormData();
    fd.append("amount", amount); fd.append("category", category);
    fd.append("expense_date", date); fd.append("description", desc);
    if (file) fd.append("receipt", file);
    setProgress(0);
    try {
      await api.post("/reimbursements", fd, { onUploadProgress: (e) => e.total && setProgress(Math.round((e.loaded / e.total) * 100)) });
      toast.success("Reimbursement request submitted");
      onCreated?.(); onClose();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); setProgress(null); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="reimbursement-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Request reimbursement</DialogTitle>
          <DialogDescription>Attach a receipt photo or PDF if you have one.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2"><Label>Amount {cur && `(${cur})`}</Label><Input type="number" inputMode="decimal" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" data-testid="reimb-amount-input" /></div>
            <div className="space-y-2"><Label>Expense date</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="reimb-date-input" /></div>
          </div>
          <div className="space-y-2">
            <Label>Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="reimb-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>{REIMB_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-2"><Label>Description</Label><Textarea value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="What was this expense for?" data-testid="reimb-description-input" /></div>
          <div className="space-y-2">
            <Label>Receipt (optional)</Label>
            {file ? (
              <div className="flex items-center gap-2 text-sm bg-slate-50 border border-slate-200 rounded-lg px-3 py-2" data-testid="reimb-file-chip">
                <Paperclip className="w-4 h-4 text-slate-400" /><span className="truncate flex-1">{file.name}</span>
                <button onClick={() => setFile(null)} className="text-slate-400 hover:text-red-500" data-testid="reimb-file-remove"><X className="w-4 h-4" /></button>
              </div>
            ) : (
              <label className="flex items-center justify-center gap-2 border border-dashed border-slate-300 rounded-lg py-3 text-sm text-slate-500 cursor-pointer hover:border-indigo-400 hover:text-indigo-600 transition-colors">
                <Paperclip className="w-4 h-4" /> Attach photo or PDF
                <input type="file" accept="image/jpeg,image/png,image/webp,image/heic,application/pdf" className="hidden" onChange={(e) => pick(e.target.files?.[0])} data-testid="reimb-file-input" />
              </label>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={progress !== null} className="bg-indigo-600 hover:bg-indigo-700" data-testid="reimb-submit-button">
            {progress !== null ? (file ? `Uploading ${progress}%` : "Submitting…") : "Submit request"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
